import { Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { AlertTriangle, Menu } from 'lucide-react';
import { Sidebar } from './Sidebar';
import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/lib/auth';

/**
 * AppShell — owns the responsive scaffold:
 *
 *   mobile  (< 768px):  Sidebar is an off-canvas drawer; hamburger trigger floats
 *                       in main; tap-outside backdrop closes it.
 *   tablet  (768–1279): Sidebar is a static icon-rail (no drawer, no hamburger).
 *   desktop (>= 1280):  Sidebar is static, user-toggleable to rail.
 *
 * Drawer state is local; auto-closes on route change and when crossing back into
 * tablet/desktop layouts (so we don't leak the open drawer past a resize).
 *
 * Impersonation banner — when a super-admin has "opened as admin" the
 * top-of-main slot renders a non-dismissable amber bar with an exit action.
 * Hidden on `/superadmin/*` paths (impersonation can't survive there since
 * super-admin nav lives in its own route, but defensive nonetheless).
 */
export function AppShell(): JSX.Element {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { location } = useRouterState();
  const navigate = useNavigate();
  const impersonation = useAuthStore((s) => s.impersonation);
  const exitImpersonation = useAuthStore((s) => s.exitImpersonation);

  // Close drawer on route change so navigation doesn't leave it open.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // Close drawer the moment we leave mobile (>= 768px). Avoids leftover state
  // when the user resizes / rotates their tablet.
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const handler = (e: MediaQueryListEvent): void => {
      if (e.matches) setDrawerOpen(false);
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Banner suppressed on the super-admin login route — there's no session yet
  // for impersonation to exist against, so this is purely defensive.
  const showBanner = Boolean(impersonation) && !location.pathname.startsWith('/superadmin/login');

  const handleExit = (): void => {
    exitImpersonation();
    void navigate({ to: '/superadmin/dashboard' });
  };

  return (
    <div className="flex h-screen w-full bg-vt-bg text-vt-text">
      {/* Backdrop — mobile drawer only */}
      {drawerOpen ? (
        <button
          type="button"
          aria-label="Close navigation"
          onClick={() => setDrawerOpen(false)}
          className="fixed inset-0 z-30 bg-black/40 backdrop-blur-sm md:hidden"
        />
      ) : null}

      <Sidebar drawerOpen={drawerOpen} onClose={() => setDrawerOpen(false)} />

      <main className="relative flex-1 overflow-y-auto">
        {/* Impersonation banner — sticky top, non-dismissable. Sits above
            sidebar overlay on mobile (z-50) so it can't be hidden by the
            drawer backdrop. */}
        {showBanner && impersonation ? (
          <div
            role="status"
            aria-live="polite"
            className="sticky top-0 z-50 flex h-10 items-center justify-between gap-vt-3 border-b border-[color:var(--vt-warn)] bg-[color:var(--vt-warn-100)] px-vt-4 text-vt-13 text-[color:var(--vt-warn)]"
          >
            <div className="flex min-w-0 items-center gap-vt-2 pl-12 md:pl-0">
              <AlertTriangle className="h-4 w-4 shrink-0" strokeWidth={2} aria-hidden="true" />
              <span className="truncate font-medium">
                Viewing as society admin · {impersonation.impersonating_society_name}
              </span>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={handleExit}
              className="h-7 shrink-0 border-[color:var(--vt-warn)] bg-vt-surface text-[color:var(--vt-warn)] hover:bg-vt-surface-2"
            >
              Exit impersonation
            </Button>
          </div>
        ) : null}

        {/* Hamburger — mobile only. Hidden while drawer is open. Position
            shifts down when the impersonation banner is visible so it
            doesn't overlap. */}
        {!drawerOpen ? (
          <button
            type="button"
            aria-label="Open navigation"
            onClick={() => setDrawerOpen(true)}
            className={`fixed left-3 z-40 inline-flex h-9 w-9 items-center justify-center rounded-vt-sm border border-vt-border bg-vt-surface text-vt-text-muted shadow-vt-1 transition-colors hover:bg-vt-surface-2 hover:text-vt-text md:hidden ${
              showBanner ? 'top-[52px]' : 'top-3'
            }`}
          >
            <Menu className="h-4 w-4" strokeWidth={1.75} />
          </button>
        ) : null}
        <Outlet />
      </main>
    </div>
  );
}
